import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom-v5-compat';
import { useSelector } from 'react-redux';
import { useErrorFocus } from '../hooks/useErrorFocus';
import Wrapper from '../layout/Wrapper';
import {
  usePostOTPVerificationMutation,
  useLazyGetAppointmentAvailabilityQuery,
} from '../redux/api/vassApi';
import {
  selectFlowType,
  selectObfuscatedEmail,
} from '../redux/slices/formSlice';
import {
  FLOW_TYPES,
  URLS,
  OTP_ERROR_CODES,
  VASS_PHONE_NUMBER,
} from '../utils/constants';
import { pathWithSearch } from '../utils/invitationQuery';
import {
  isAccountLockedError,
  isServerError,
  isAppointmentAlreadyBookedError,
  isAppointmentNotFoundError,
  isNotWhithinCohortError,
  isNoSlotsAvailableError,
} from '../utils/errors';

const getErrorMessage = (errorCode, attemptsRemaining = 0) => {
  switch (errorCode) {
    case OTP_ERROR_CODES.ACCOUNT_LOCKED:
      return 'The one-time passcode you entered doesn’t match the one we sent you. You can try again in 15 minutes. Check your email and select the link to schedule a call.';
    case OTP_ERROR_CODES.OTP_EXPIRED:
      return 'The one-time passcode you entered has expired. Select the link in your email to get a new code and schedule a call.';
    case OTP_ERROR_CODES.INVALID_OTP:
      if (attemptsRemaining === 1) {
        return 'The one-time passcode you entered doesn’t match the one we sent you. You have 1 try left. Then you’ll need to wait 15 minutes before trying again.';
      }
      return 'The one-time passcode you entered doesn’t match the one we sent you. Check your email and try again.';
    default:
      return undefined;
  }
};

const validateOTP = otp => {
  if (otp === '') return 'Please enter your one-time passcode';
  if (!/^\d+$/.test(otp))
    return 'Your one-time passcode should only contain numbers';
  if (otp.length !== 6) return 'Your one-time passcode should be 6 digits';
  return null;
};

const getPageTitle = (cancellationFlow, hasError) => {
  if (hasError) {
    return 'We couldn’t verify your information';
  }
  if (cancellationFlow) {
    return 'Cancel VA Solid Start call';
  }
  return 'Schedule a call with VA Solid Start';
};

const EnterOTP = () => {
  const flowType = useSelector(selectFlowType);
  const cancellationFlow = flowType === FLOW_TYPES.CANCEL;
  const navigate = useNavigate();
  const { search } = useLocation();
  const obfuscatedEmail = useSelector(selectObfuscatedEmail);
  const [
    { error: otpError, handleSetError: setOtpError },
    { handleSetError: setApiError },
  ] = useErrorFocus([
    'va-text-input[name="otp"]',
    'va-alert[data-testid="enter-otp-error-alert"]',
  ]);

  const [code, setCode] = useState('');
  const [noAppointmentToCancel, setNoAppointmentToCancel] = useState(false);

  const [postOTPVerification, { isLoading, error: postOTPVerificationError }] =
    usePostOTPVerificationMutation();
  const [
    getAppointmentAvailability,
    { isFetching: isCheckingAvailability, error: appointmentAvailabilityError },
  ] = useLazyGetAppointmentAvailabilityQuery();

  const handleSubmit = async () => {
    const validationError = validateOTP(code);
    if (validationError) {
      setOtpError(validationError);
      return;
    }

    const response = await postOTPVerification({ otp: code });
    if (response.error) {
      setApiError('API Error');
      return;
    }

    const { error } = await getAppointmentAvailability();

    if (
      isServerError(error) ||
      isNotWhithinCohortError(error) ||
      isNoSlotsAvailableError(error) ||
      isAppointmentNotFoundError(error)
    ) {
      return;
    }

    if (isAppointmentAlreadyBookedError(error)) {
      const { appointmentId } = error.appointment;
      const url = cancellationFlow
        ? `${URLS.CANCEL_APPOINTMENT}/${appointmentId}`
        : `${URLS.ALREADY_SCHEDULED}/${appointmentId}`;
      navigate(pathWithSearch(url, search), { replace: true });
      return;
    }

    if (cancellationFlow) {
      setNoAppointmentToCancel(true);
      return;
    }

    navigate(pathWithSearch(URLS.DATE_TIME, search), { replace: true });
  };

  const errorMessage = getErrorMessage(
    postOTPVerificationError?.code,
    postOTPVerificationError?.attemptsRemaining,
  );
  const pageTitle = getPageTitle(
    cancellationFlow,
    isAccountLockedError(postOTPVerificationError),
  );

  return (
    <Wrapper
      pageTitle={pageTitle}
      verificationError={
        isAccountLockedError(postOTPVerificationError)
          ? errorMessage
          : undefined
      }
      flowType={flowType}
      errorAlert={
        isServerError(postOTPVerificationError) ||
        isServerError(appointmentAvailabilityError) ||
        isNotWhithinCohortError(appointmentAvailabilityError) ||
        isNoSlotsAvailableError(appointmentAvailabilityError) ||
        isAppointmentNotFoundError(appointmentAvailabilityError) ||
        noAppointmentToCancel
      }
    >
      {!postOTPVerificationError?.code && (
        <va-alert
          status="success"
          visible
          data-testid="enter-otp-success-alert"
        >
          <h2 slot="headline">We’ve emailed you a one-time passcode</h2>
          <p
            className="vads-u-margin-y--0 vads-u-margin-bottom--2"
            data-dd-privacy="mask"
          >
            {`We emailed a one-time passcode (OTP) to ${obfuscatedEmail}. Enter the code here to complete your verification process and ${
              cancellationFlow ? 'cancel' : 'schedule'
            } your call.`}
          </p>
          <p className="vads-u-margin-y--0">
            <strong>Note:</strong> If you don’t receive the OTP, request a new
            code using the link in your original email. Or call us at{' '}
            <va-telephone
              contact={VASS_PHONE_NUMBER}
              data-testid="solid-start-telephone"
            />{' '}
            to schedule. We’re here Monday through Friday, 8:00 a.m. to 9:00
            p.m. ET.
          </p>
        </va-alert>
      )}

      {postOTPVerificationError?.code && (
        <va-alert status="error" visible data-testid="enter-otp-error-alert">
          <p className="vads-u-margin-y--0">{errorMessage}</p>
        </va-alert>
      )}
      <va-text-input
        data-dd-privacy="mask"
        class="vads-u-margin-top--4"
        label="Enter your one-time passcode"
        name="otp"
        value={code}
        inputmode="numeric"
        maxlength="6"
        onBlur={e => {
          if (e.target.value !== '') {
            setOtpError('');
          }
        }}
        onInput={e => {
          setCode(e.target.value);
        }}
        required
        error={otpError}
        data-testid="otp-input"
        autocomplete="one-time-code"
        show-input-error
      />
      <div className="vads-u-display--flex vads-u-margin-top--4 vass-form__button-container vass-flex-direction--column">
        <va-button
          big
          onClick={handleSubmit}
          text="Continue"
          data-testid="continue-button"
          uswds
          loading={isLoading || isCheckingAvailability}
        />
      </div>
    </Wrapper>
  );
};

export default EnterOTP;
